/**
 * Internationalization (i18n) module
 *
 * Supports 'en' (English) and 'de' (German).
 * Language is read from localStorage key 'user_language'.
 * Falls back to 'en' when the key is absent or unrecognised.
 */

const LANG_KEY = 'user_language';
const SUPPORTED_LANGS = ['en', 'de'];

const translations = {
  en: {
    // General
    logout: 'Logout',
    error: 'Error',
    retry: 'Retry',

    // Loading
    searching_contacts: 'Searching for contacts...',
    default_email_subject: 'Email',

    // No item selected
    no_item_selected: 'No email or appointment selected. Please open an email or appointment to use this add-in.',

    // Login
    login_title: 'Login to AssistGo CRM',
    login_description: 'AssistGo CRM connects your Outlook inbox and calendar directly to your CRM. Link emails and appointments to contacts and opportunities with a single click — no copy-pasting, no switching tabs.',
    sso_button: 'Sign in with Microsoft',
    or: 'or',
    instance_label: 'Instance Name',
    instance_placeholder: 'e.g. dev, staging, prod',
    email_label: 'Email',
    password_label: 'Password',
    login_button: 'Login with Email',
    login_new_user: 'New to AssistGo CRM?',
    login_request_demo: 'Request a demo or trial account',
    login_enterprise_text: 'Access is provided through your organisation. Contact',
    login_enterprise_text2: 'or your administrator to request an account.',
    privacy_policy: 'Privacy Policy',
    instance_required: 'Instance name is required',
    instance_required_sso: 'Instance name is required for SSO',
    login_failed: 'Login failed. Please try again.',
    sso_login_failed: 'SSO login failed. Please try again.',
    signing_in: 'Signing in...',
    device_verification_support: 'Device verification required. Please contact support.',

    // Device Verification
    device_verification_title: 'Device Verification Required',
    device_verification_body: 'You have recently logged in from a new device. Please enter the 6-digit verification code sent to your email to continue.',
    verification_code_label: 'Verification Code',
    verification_code_placeholder: '123456',
    cancel: 'Cancel',
    verify_device: 'Verify Device',
    verifying: 'Verifying...',
    invalid_code: 'Please enter a valid 6-digit verification code',
    verification_failed: 'Verification failed. Please try again.',

    // Import Status
    already_imported: 'Already imported',
    not_imported_yet: 'Not imported yet',
    view_activity: 'View Activity',
    imported_on: 'on',

    // Wizard – Contacts
    contacts_title: '👥 Select Contacts (Optional)',
    contacts_desc_email: 'Choose which contacts to associate with this email',
    contacts_desc_appointment: 'Choose which contacts to associate with this appointment',
    contact_search_label: 'Search for contacts:',
    contact_search_placeholder: 'Type to search contacts by name, email, or company...',
    search_another_contact: 'Search for another CRM contact',
    unknown_contact: 'Unknown Contact',
    selected_contacts_header: 'Selected Contacts:',
    contacts_selected_one: 'contact selected',
    contacts_selected_many: 'contacts selected',
    extract_signature_button: 'Add contact from signature',
    add_contact_from_signature_button: 'Add contact from signature',
    signature_detected_status: 'Detected · edit if needed',
    signature_fallback_status: "Couldn't detect a signature. Select it in the email and paste below.",
    signature_loading: 'Looking for signature…',
    signature_copy_button: 'Copy',
    signature_paste_button: 'Paste',
    signature_copied: 'Signature copied',
    signature_paste_failed: "Couldn't paste — use Ctrl/Cmd+V instead",
    signature_create_contact_button: 'Create contact in CRM',
    signature_create_contact_hint: 'Paste or extract a signature first',
    signature_create_contact_no_instance: 'Sign in to your CRM workspace first',

    // Wizard – Leads & Campaigns
    leads_campaigns_title: '🎯 Leads & Campaigns',
    leads_campaigns_desc: 'Optionally select a lead and campaign to associate',
    lead_label: 'Lead:',
    campaign_label: 'Campaign:',
    select_lead: 'Select Lead',
    select_campaign: 'Select Campaign',
    unnamed_lead: 'Unnamed Lead',
    unnamed_campaign: 'Unnamed Campaign',
    multiple_contacts: 'Multiple Contacts',
    click_to_open_lead: 'Click to open lead details',
    click_to_open_campaign: 'Click to open campaign details',

    // Wizard – Activity type
    activity_type_title: '📋 Activity type',
    activity_type_desc: 'Optional. Leave this blank to use the default for this email or appointment.',
    activity_type_label: 'Type:',
    activity_type_placeholder: 'Select activity type',

    // Wizard – Notify
    notify_title: '🔔 Notify Users',
    notify_desc: 'Select users to be notified about this activity',
    notify_label: 'Notify:',
    notify_placeholder: 'Search and select users...',
    no_users_selected: 'No users selected',
    unknown_user: 'Unknown User',
    failed_load_users: 'Failed to load users:',

    // Wizard – Attachments
    attachments_title: '📎 Attachments',
    attachments_desc: 'Choose attachments to include with the import',
    attachment_not_supported_title: 'Attachment Upload Not Supported',
    attachment_not_supported_body: "Your Exchange server doesn't support attachment content access.",
    found_attachments: 'Found attachments:',
    select_all: 'All',
    select_none: 'None',
    email_msg_always_included: '(Email as .msg file - always included)',
    failed_load_attachments: 'Failed to load attachments:',

    // Wizard – Navigation
    import_to_crm: '🚀 Import to CRM',
    importing: 'Importing...',

    // Success Screen
    email_added_title: 'Email Added Successfully!',
    event_added_title: 'Calendar Event Added Successfully!',
    email_saved_subtitle: 'Your email has been saved to CRM.',
    event_saved_subtitle: 'Your event has been saved to CRM.',
    subject_label: 'Subject:',
    from_label: 'From:',
    date_label: 'Date:',
    contacts_label: 'Contacts:',
    lead_result_label: 'Lead:',
    campaign_result_label: 'Campaign:',
    attachments_label: 'Attachments:',
    uploaded: 'uploaded',
    linked: 'Linked',
    selected: 'Selected',
    view_in_crm: '🔗 View in CRM',
    import_another: '➕ Import Another',

    // Error Screen
    error_adding_email: 'Error Adding Email',
    error_adding_event: 'Error Adding Calendar Event',
    try_again: '🔄 Try Again',

    // Empty States
    no_contacts_title: 'No Contacts Found',
    no_contacts_msg_email: 'No contacts were found for the email addresses in this email.',
    no_contacts_msg_appointment: 'No contacts were found for the email addresses in this appointment.',
    no_leads_title: 'No Leads Found',
    no_leads_msg: 'No active leads were found for the selected contacts.',
    no_campaigns_title: 'No Campaigns Found',
    no_campaigns_msg: 'No campaigns were found for the selected contacts.',
    no_attachments_title: 'No Attachments',
    no_attachments_msg: "This email doesn't have any file attachments.",

    // AI Assistant – Floating Button
    ai_button_label: 'Ask AI Assistant',
    ai_button_aria: 'Open AI Assistant',

    // AI Assistant – Drawer / Header
    ai_assistant_label: 'Assistant',
    ai_minimize_title: 'Minimize',
    ai_expand_title: 'Expand',
    ai_close_title: 'Close',
    ai_back_title: 'Back to Home',
    ai_toggle_sidebar_title: 'Toggle Conversations',
    ai_new_chat: 'New Chat',
    ai_conversation_title: 'Conversation',
    ai_new_conversation: 'New Conversation',
    ai_loading_messages: 'Loading messages...',
    ai_delete_confirm: 'Are you sure you want to delete this conversation?',
    ai_delete_failed: 'Failed to delete conversation',
    ai_prompts_failed: 'Failed to open saved prompts',
    ai_error_prefix: 'Sorry, I encountered an error:',
    ai_error_suffix: 'Please try again.',
    ai_thinking: 'Thinking…',
    ai_retry: 'Retry',
    ai_no_access: "You don't have access to this record",
    ai_restricted_record: 'Restricted record',
    ai_unsupported_block: 'Unsupported content',
    ai_choice_free_text_hint: 'Or type your own answer',
    ai_references_label: 'References',
    ai_related_record_one: '1 related record',
    ai_related_records_many: '{count} related records',
    ai_feedback_skip: 'Skip',
    ai_feedback_submit: 'Submit',
    ai_feedback_thumbs_up: 'Thumbs up',
    ai_feedback_thumbs_down: 'Thumbs down',
    ai_feedback_conversation_title: 'Rate this conversation',
    ai_feedback_rate_stars: 'Rate {n} stars',
    ai_feedback_note_prompt: 'What went wrong? (optional)',
    ai_feedback_note_placeholder: 'Tell us what could be improved...',

    ai_sheet_label: 'Clarification',
    ai_sheet_previous: 'Previous question',
    ai_sheet_next: 'Next question',
    ai_sheet_skip: 'Skip',
    ai_sheet_dismiss: 'Dismiss',
    ai_sheet_send: 'Send',
    ai_sheet_free_text_label: 'Answer in your own words',
    ai_sheet_free_text_placeholder: 'Something else',
    ai_sheet_free_text_submit: 'Use this answer',

    ai_form_shared_label: 'Shared',
    ai_prompt_shared: 'Shared',

    ai_usage_limit_title: 'Monthly AI usage limit reached',
    ai_usage_limit_body_1: 'Your workspace has used all of its monthly AI credits, so <strong>AI Assistant</strong> is temporarily unavailable.',
    ai_usage_limit_body_2: 'AI Assistant and Data Enrichment share the same monthly credit pool. Please contact your workspace administrator to increase your plan, or wait until the credits reset at the start of next month.',
    ai_usage_limit_ok: 'Got it',

    // AI Assistant – Welcome
    ai_welcome_title: 'Welcome to AI Assistant',
    ai_welcome_body: "I'm here to help you work with your CRM. Ask me about contacts, deals, reports, or get assistance with any CRM tasks.",
    ai_suggestion_1: 'Show me my activities due today and overdue',
    ai_suggestion_2: 'Create an activity',
    ai_suggestion_3: 'Create a lead',

    // AI Assistant – Chat Input
    ai_chat_placeholder: 'Type your message...',
    ai_chat_hint: 'Press Enter to send, Shift + Enter for new line',
    ai_prompts_button_title: 'Saved Prompts',

    ai_show_more: 'Show {n} more',
    ai_summarize_these: 'Summarize these {n}',
    ai_summarize: 'Summarize',
    ai_comment: 'Comment',
    ai_clear_pin: 'Clear selection',
    ai_open_record: 'Open record',
    ai_messages_apply_to: 'Messages apply to #{n}',
    ai_comment_placeholder: 'Comment on {label}…',
    ai_list_window: '{from}–{to} of {total}',
    ai_list_window_open: '{from}–{to}',
    ai_confirm: 'Confirm',
    ai_review_changes: 'Review',
    ai_changed_since: 'Changed since you asked',
    ai_hint_pinned: 'Messages apply to #{n} until you clear it',
    ai_hint_comment: 'Enter posts the comment',
    ai_entity_activity: 'Activity',
    ai_entity_contact: 'Contact',
    ai_entity_lead: 'Lead',
    ai_entity_customer: 'Company',
    ai_entity_campaign: 'Campaign',
    ai_entity_user: 'User',

    // AI Assistant – Sidebar
    ai_conversations_title: 'Conversations',
    ai_bookmarked: 'Bookmarked',
    ai_all_conversations: 'All Conversations',
    ai_loading_conversations: 'Loading conversations...',
    ai_no_conversations: 'No conversations yet',
    ai_start_chat_hint: 'Start a new chat to begin',
    ai_new_chat_btn: 'New Chat',

    // AI Assistant – Conversation List Item
    ai_remove_bookmark: 'Remove bookmark',
    ai_add_bookmark: 'Add bookmark',
    ai_delete_conversation_title: 'Delete conversation',
    ai_msgs_suffix: 'msgs',

    // AI Assistant – Saved Prompts Modal
    ai_saved_prompts_title: 'Saved Prompts',
    ai_search_prompts_placeholder: 'Search prompts...',
    ai_new_prompt_title: 'New Prompt',
    ai_edit_prompt_title: 'Edit Prompt',
    ai_loading_prompts: 'Loading prompts...',
    ai_no_matching_prompts: 'No matching prompts',
    ai_no_saved_prompts: 'No saved prompts yet',
    ai_create_first_prompt: 'Create your first prompt to get started',
    ai_used_times: 'Used {n} times',
    ai_edit_title: 'Edit',
    ai_delete_title: 'Delete',
    ai_form_title_label: 'Title',
    ai_form_prompt_label: 'Prompt',
    ai_form_prompt_placeholder: 'Enter your prompt template...',
    ai_form_category_label: 'Category',
    ai_form_category_placeholder: 'e.g., Writing, Coding, Analysis',
    ai_form_favorite_label: 'Mark as favorite',
    ai_cancel: 'Cancel',
    ai_save_changes: 'Save Changes',
    ai_create_prompt: 'Create Prompt',
    ai_save_prompt_failed: 'Failed to save prompt',
    ai_delete_prompt_confirm: 'Are you sure you want to delete this prompt?',
    ai_delete_prompt_failed: 'Failed to delete prompt',
    ai_enter_title: 'Please enter a title',
    ai_enter_prompt: 'Please enter a prompt',
    ai_filter_all: 'All',

    // AI – Relative times
    just_now: 'Just now',
    minutes_ago: '{n}m ago',
    hours_ago: '{n}h ago',
    yesterday: 'Yesterday',
    days_ago: '{n}d ago',
    today: 'Today',
    this_week: 'This Week',
    this_month: 'This Month',

    // AI – Message count
    no_messages: 'No messages',
    one_message: '1 message',
    n_messages: '{n} messages',

    // Searchable dropdown
    dropdown_searching: 'Searching...',
    dropdown_no_results: 'No results found matching "{q}"',
    dropdown_type_to_search: 'Type to search',
    dropdown_min_chars: 'Type at least {n} characters to search...',
    dropdown_none: 'None',
    dropdown_unknown: 'Unknown',
    dropdown_search_failed: 'Search failed: {message}',

    // Two-Factor Authentication
    twoFactorSetupTitle: 'Set Up Two-Factor Authentication',
    twoFactorVerifyTitle: 'Two-Factor Authentication',
    twoFactorStepScan: 'Scan',
    twoFactorStepConfirm: 'Confirm',
    twoFactorStepSaveCodes: 'Save Codes',
    twoFactorStepVerify: 'Verify',
    twoFactorScanQrCode: 'Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)',
    twoFactorManualEntry: "Can't scan? Enter this key manually:",
    twoFactorScannedQr: "I've scanned the QR code",
    twoFactorEnterCodeConfirm: 'Enter the 6-digit code from your authenticator app to confirm setup:',
    twoFactorVerifyAndEnable: 'Verify & Enable',
    twoFactorSaveRecoveryCodes: 'Save your recovery codes!',
    twoFactorRecoveryCodesDesc: 'These codes can be used to access your account if you lose your authenticator device. Each code can only be used once. Store them in a safe place.',
    twoFactorCopyAllCodes: 'Copy all codes',
    twoFactorCodesSaved: "I've saved these codes",
    twoFactorEnterNewCode: 'Enter a new code from your authenticator app to complete setup:',
    twoFactorCompleteSetup: 'Complete Setup',
    twoFactorEnterCode: 'Enter the 6-digit code from your authenticator app:',
    twoFactorUseRecovery: 'Use recovery code instead',
    twoFactorUseAuthenticator: 'Use authenticator code instead',
    twoFactorEnterRecovery: 'Enter one of your recovery codes:',
    twoFactorVerifyRecovery: 'Verify with Recovery Code',
    twoFactorBackToLogin: 'Back to login',
    twoFactorSetupFailed: 'Failed to initialize 2FA setup. Please try logging in again.',
    twoFactorInvalidCode: 'Invalid verification code. Please try again.',
    twoFactorEnterSixDigit: 'Please enter a 6-digit code',
    twoFactorEnterRecoveryCode: 'Please enter a recovery code',
    twoFactorEnabledSuccess: 'Two-factor authentication enabled successfully!',
    twoFactorVerifySuccess: 'Verification successful!',
    twoFactorSecretCopied: 'Secret copied to clipboard',
    twoFactorCodesCopied: 'Recovery codes copied to clipboard',

    // Clipboard
    phone_copied: 'Phone number copied: {text}',

    // Contact card
    contact_click_copy: 'Click to copy',
    contact_view_details: 'View Details',
    contact_hide_details: 'Hide Details',
    contact_section_personal: 'Personal Information',
    contact_label_name: 'Name:',
    contact_label_position: 'Position:',
    contact_label_department: 'Department:',
    contact_label_salutation: 'Salutation:',
    contact_label_academic_title: 'Academic Title:',
    contact_section_contact: 'Contact Information',
    contact_label_emails: 'Emails:',
    contact_label_phones: 'Phone Numbers:',
    contact_label_linkedin: 'LinkedIn:',
    contact_phone_type: 'Phone',
    contact_section_company: 'Company Information',
    contact_label_company: 'Company:',
    contact_label_branch: 'Branch:',
    contact_label_address: 'Address:',
    contact_recent_activities: 'Recent Activities ({n})',
    contact_show_more_activities: 'Show {n} more activities',
    contact_show_less: 'Show less',
    contact_untitled: 'Untitled',
    contact_activity_open_title: 'Click to open activity',
    not_applicable: 'N/A',
  },

  de: {
    // General
    logout: 'Abmelden',
    error: 'Fehler',
    retry: 'Erneut versuchen',

    // Loading
    searching_contacts: 'Kontakte werden gesucht...',
    default_email_subject: 'E-Mail',

    // No item selected
    no_item_selected: 'Keine E-Mail oder kein Termin ausgewählt. Bitte öffnen Sie eine E-Mail oder einen Termin, um dieses Add-in zu verwenden.',

    // Login
    login_title: 'Bei AssistGo CRM anmelden',
    login_description: 'AssistGo CRM verbindet Ihren Outlook-Posteingang und Kalender direkt mit Ihrem CRM. Verknüpfen Sie E-Mails und Termine mit Kontakten und Opportunitys mit einem einzigen Klick – kein Kopieren, kein Tab-Wechsel.',
    sso_button: 'Mit Microsoft anmelden',
    or: 'oder',
    instance_label: 'Instanzname',
    instance_placeholder: 'z.B. dev, staging, prod',
    email_label: 'E-Mail',
    password_label: 'Passwort',
    login_button: 'Mit E-Mail anmelden',
    login_new_user: 'Neu bei AssistGo CRM?',
    login_request_demo: 'Demo oder Testzugang anfragen',
    login_enterprise_text: 'Der Zugang wird über Ihre Organisation bereitgestellt. Kontaktieren Sie',
    login_enterprise_text2: 'oder Ihren Administrator, um ein Konto anzufordern.',
    privacy_policy: 'Datenschutzerklärung',
    instance_required: 'Instanzname ist erforderlich',
    instance_required_sso: 'Instanzname ist für SSO erforderlich',
    login_failed: 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.',
    sso_login_failed: 'SSO-Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.',
    signing_in: 'Anmeldung läuft...',
    device_verification_support: 'Geräteverifizierung erforderlich. Bitte kontaktieren Sie den Support.',

    // Device Verification
    device_verification_title: 'Geräteverifizierung erforderlich',
    device_verification_body: 'Sie haben sich kürzlich von einem neuen Gerät angemeldet. Bitte geben Sie den 6-stelligen Bestätigungscode ein, der an Ihre E-Mail gesendet wurde.',
    verification_code_label: 'Bestätigungscode',
    verification_code_placeholder: '123456',
    cancel: 'Abbrechen',
    verify_device: 'Gerät verifizieren',
    verifying: 'Verifizierung läuft...',
    invalid_code: 'Bitte geben Sie einen gültigen 6-stelligen Bestätigungscode ein',
    verification_failed: 'Verifizierung fehlgeschlagen. Bitte versuchen Sie es erneut.',

    // Import Status
    already_imported: 'Bereits importiert',
    not_imported_yet: 'Noch nicht importiert',
    view_activity: 'Aktivität anzeigen',
    imported_on: 'am',

    // Wizard – Contacts
    contacts_title: '👥 Kontakte auswählen (Optional)',
    contacts_desc_email: 'Wählen Sie Kontakte aus, die mit dieser E-Mail verknüpft werden sollen',
    contacts_desc_appointment: 'Wählen Sie Kontakte aus, die mit diesem Termin verknüpft werden sollen',
    contact_search_label: 'Kontakte suchen:',
    contact_search_placeholder: 'Name, E-Mail oder Unternehmen eingeben...',
    search_another_contact: 'Weiteren CRM-Kontakt suchen',
    unknown_contact: 'Unbekannter Kontakt',
    selected_contacts_header: 'Ausgewählte Kontakte:',
    contacts_selected_one: 'Kontakt ausgewählt',
    contacts_selected_many: 'Kontakte ausgewählt',
    extract_signature_button: 'Kontakt aus Signatur hinzufügen',
    add_contact_from_signature_button: 'Kontakt aus Signatur hinzufügen',
    signature_detected_status: 'Erkannt · bei Bedarf bearbeiten',
    signature_fallback_status: 'Signatur konnte nicht erkannt werden. Markieren Sie sie in der E-Mail und fügen Sie sie unten ein.',
    signature_loading: 'Suche nach Signatur…',
    signature_copy_button: 'Kopieren',
    signature_paste_button: 'Einfügen',
    signature_copied: 'Signatur kopiert',
    signature_paste_failed: 'Einfügen nicht möglich — bitte Strg/Cmd+V verwenden',
    signature_create_contact_button: 'Kontakt im CRM erstellen',
    signature_create_contact_hint: 'Bitte zuerst eine Signatur einfügen oder extrahieren',
    signature_create_contact_no_instance: 'Melden Sie sich zuerst in Ihrem CRM-Arbeitsbereich an',

    // Wizard – Leads & Campaigns
    leads_campaigns_title: '🎯 Leads & Kampagnen',
    leads_campaigns_desc: 'Optional einen Lead und eine Kampagne zuordnen',
    lead_label: 'Lead:',
    campaign_label: 'Kampagne:',
    select_lead: 'Lead auswählen',
    select_campaign: 'Kampagne auswählen',
    unnamed_lead: 'Namenloser Lead',
    unnamed_campaign: 'Namenlose Kampagne',
    multiple_contacts: 'Mehrere Kontakte',
    click_to_open_lead: 'Lead-Details öffnen',
    click_to_open_campaign: 'Kampagnen-Details öffnen',

    // Wizard – Activity type
    activity_type_title: '📋 Aktivitätstyp',
    activity_type_desc: 'Optional. Leer lassen, um den Standard für diese E-Mail oder diesen Termin zu verwenden.',
    activity_type_label: 'Typ:',
    activity_type_placeholder: 'Aktivitätstyp auswählen',

    // Wizard – Notify
    notify_title: '🔔 Benutzer benachrichtigen',
    notify_desc: 'Wählen Sie Benutzer aus, die über diese Aktivität benachrichtigt werden sollen',
    notify_label: 'Benachrichtigen:',
    notify_placeholder: 'Benutzer suchen und auswählen...',
    no_users_selected: 'Keine Benutzer ausgewählt',
    unknown_user: 'Unbekannter Benutzer',
    failed_load_users: 'Benutzer konnten nicht geladen werden:',

    // Wizard – Attachments
    attachments_title: '📎 Anhänge',
    attachments_desc: 'Wählen Sie Anhänge für den Import aus',
    attachment_not_supported_title: 'Anhang-Upload nicht unterstützt',
    attachment_not_supported_body: 'Ihr Exchange-Server unterstützt keinen Zugriff auf Anhangsinhalte.',
    found_attachments: 'Gefundene Anhänge:',
    select_all: 'Alle',
    select_none: 'Keine',
    email_msg_always_included: '(E-Mail als .msg-Datei – immer enthalten)',
    failed_load_attachments: 'Anhänge konnten nicht geladen werden:',

    // Wizard – Navigation
    import_to_crm: '🚀 In CRM importieren',
    importing: 'Importieren...',

    // Success Screen
    email_added_title: 'E-Mail erfolgreich hinzugefügt!',
    event_added_title: 'Termin erfolgreich hinzugefügt!',
    email_saved_subtitle: 'Ihre E-Mail wurde im CRM gespeichert.',
    event_saved_subtitle: 'Ihr Termin wurde im CRM gespeichert.',
    subject_label: 'Betreff:',
    from_label: 'Von:',
    date_label: 'Datum:',
    contacts_label: 'Kontakte:',
    lead_result_label: 'Lead:',
    campaign_result_label: 'Kampagne:',
    attachments_label: 'Anhänge:',
    uploaded: 'hochgeladen',
    linked: 'Verknüpft',
    selected: 'Ausgewählt',
    view_in_crm: '🔗 In CRM anzeigen',
    import_another: '➕ Weiteren importieren',

    // Error Screen
    error_adding_email: 'Fehler beim Hinzufügen der E-Mail',
    error_adding_event: 'Fehler beim Hinzufügen des Termins',
    try_again: '🔄 Erneut versuchen',

    // Empty States
    no_contacts_title: 'Keine Kontakte gefunden',
    no_contacts_msg_email: 'Für die E-Mail-Adressen in dieser E-Mail wurden keine Kontakte gefunden.',
    no_contacts_msg_appointment: 'Für die E-Mail-Adressen in diesem Termin wurden keine Kontakte gefunden.',
    no_leads_title: 'Keine Leads gefunden',
    no_leads_msg: 'Für die ausgewählten Kontakte wurden keine aktiven Leads gefunden.',
    no_campaigns_title: 'Keine Kampagnen gefunden',
    no_campaigns_msg: 'Für die ausgewählten Kontakte wurden keine Kampagnen gefunden.',
    no_attachments_title: 'Keine Anhänge',
    no_attachments_msg: 'Diese E-Mail hat keine Dateianhänge.',

    // AI Assistant – Floating Button
    ai_button_label: 'KI-Assistent fragen',
    ai_button_aria: 'KI-Assistenten öffnen',

    // AI Assistant – Drawer / Header
    ai_assistant_label: 'Assistent',
    ai_minimize_title: 'Minimieren',
    ai_expand_title: 'Erweitern',
    ai_close_title: 'Schließen',
    ai_back_title: 'Zurück zur Startseite',
    ai_toggle_sidebar_title: 'Gespräche ein-/ausblenden',
    ai_new_chat: 'Neuer Chat',
    ai_conversation_title: 'Gespräch',
    ai_new_conversation: 'Neues Gespräch',
    ai_loading_messages: 'Nachrichten werden geladen...',
    ai_delete_confirm: 'Möchten Sie dieses Gespräch wirklich löschen?',
    ai_delete_failed: 'Gespräch konnte nicht gelöscht werden',
    ai_prompts_failed: 'Gespeicherte Prompts konnten nicht geöffnet werden',
    ai_error_prefix: 'Entschuldigung, ein Fehler ist aufgetreten:',
    ai_error_suffix: 'Bitte versuchen Sie es erneut.',
    ai_thinking: 'Denke nach…',
    ai_retry: 'Erneut versuchen',
    ai_no_access: 'Sie haben keinen Zugriff auf diesen Datensatz',
    ai_restricted_record: 'Eingeschränkter Datensatz',
    ai_unsupported_block: 'Nicht unterstützter Inhalt',
    ai_choice_free_text_hint: 'Oder geben Sie Ihre eigene Antwort ein',
    ai_references_label: 'Referenzen',
    ai_related_record_one: '1 zugehöriger Datensatz',
    ai_related_records_many: '{count} zugehörige Datensätze',
    ai_feedback_skip: 'Überspringen',
    ai_feedback_submit: 'Senden',
    ai_feedback_thumbs_up: 'Daumen hoch',
    ai_feedback_thumbs_down: 'Daumen runter',
    ai_feedback_conversation_title: 'Bewerten Sie dieses Gespräch',
    ai_feedback_rate_stars: '{n} Sterne bewerten',
    ai_feedback_note_prompt: 'Was war das Problem? (optional)',
    ai_feedback_note_placeholder: 'Erzählen Sie uns, was verbessert werden könnte...',

    ai_sheet_label: 'Rückfrage',
    ai_sheet_previous: 'Vorherige Frage',
    ai_sheet_next: 'Nächste Frage',
    ai_sheet_skip: 'Überspringen',
    ai_sheet_dismiss: 'Verwerfen',
    ai_sheet_send: 'Senden',
    ai_sheet_free_text_label: 'Mit eigenen Worten antworten',
    ai_sheet_free_text_placeholder: 'Etwas anderes',
    ai_sheet_free_text_submit: 'Diese Antwort verwenden',

    ai_form_shared_label: 'Geteilt',
    ai_prompt_shared: 'Geteilt',

    ai_usage_limit_title: 'Monatliches KI-Nutzungslimit erreicht',
    ai_usage_limit_body_1: 'Ihr Arbeitsbereich hat alle monatlichen KI-Credits aufgebraucht, daher ist der <strong>KI-Assistent</strong> vorübergehend nicht verfügbar.',
    ai_usage_limit_body_2: 'KI-Assistent und Datenanreicherung teilen sich denselben monatlichen Credit-Pool. Bitte wenden Sie sich an Ihren Administrator, um den Plan zu erhöhen, oder warten Sie auf den Reset zum Monatsbeginn.',
    ai_usage_limit_ok: 'Verstanden',

    // AI Assistant – Welcome
    ai_welcome_title: 'Willkommen beim KI-Assistenten',
    ai_welcome_body: 'Ich bin hier, um Ihnen bei der Arbeit mit Ihrem CRM zu helfen. Fragen Sie mich nach Kontakten, Geschäften, Berichten oder erhalten Sie Hilfe bei allen CRM-Aufgaben.',
    ai_suggestion_1: 'Zeige mir meine heutigen und überfälligen Aktivitäten',
    ai_suggestion_2: 'Erstelle eine Aktivität',
    ai_suggestion_3: 'Erstelle ein Lead',

    // AI Assistant – Chat Input
    ai_chat_placeholder: 'Nachricht eingeben...',
    ai_chat_hint: 'Enter zum Senden, Umschalt+Enter für neue Zeile',
    ai_prompts_button_title: 'Gespeicherte Prompts',

    ai_show_more: '{n} weitere anzeigen',
    ai_summarize_these: 'Diese {n} zusammenfassen',
    ai_summarize: 'Zusammenfassen',
    ai_comment: 'Kommentar',
    ai_clear_pin: 'Auswahl aufheben',
    ai_open_record: 'Datensatz öffnen',
    ai_messages_apply_to: 'Nachrichten gelten für #{n}',
    ai_comment_placeholder: 'Kommentar zu {label}…',
    ai_list_window: '{from}–{to} von {total}',
    ai_list_window_open: '{from}–{to}',
    ai_confirm: 'Bestätigen',
    ai_review_changes: 'Prüfen',
    ai_changed_since: 'Seit Ihrer Anfrage geändert',
    ai_hint_pinned: 'Nachrichten gelten für #{n}, bis Sie die Auswahl aufheben',
    ai_hint_comment: 'Enter speichert den Kommentar',
    ai_entity_activity: 'Aktivität',
    ai_entity_contact: 'Kontakt',
    ai_entity_lead: 'Lead',
    ai_entity_customer: 'Firma',
    ai_entity_campaign: 'Kampagne',
    ai_entity_user: 'Benutzer',

    // AI Assistant – Sidebar
    ai_conversations_title: 'Gespräche',
    ai_bookmarked: 'Gespeichert',
    ai_all_conversations: 'Alle Gespräche',
    ai_loading_conversations: 'Gespräche werden geladen...',
    ai_no_conversations: 'Noch keine Gespräche',
    ai_start_chat_hint: 'Starten Sie einen neuen Chat',
    ai_new_chat_btn: 'Neuer Chat',

    // AI Assistant – Conversation List Item
    ai_remove_bookmark: 'Lesezeichen entfernen',
    ai_add_bookmark: 'Lesezeichen hinzufügen',
    ai_delete_conversation_title: 'Gespräch löschen',
    ai_msgs_suffix: 'Nachr.',

    // AI Assistant – Saved Prompts Modal
    ai_saved_prompts_title: 'Gespeicherte Prompts',
    ai_search_prompts_placeholder: 'Prompts suchen...',
    ai_new_prompt_title: 'Neuer Prompt',
    ai_edit_prompt_title: 'Prompt bearbeiten',
    ai_loading_prompts: 'Prompts werden geladen...',
    ai_no_matching_prompts: 'Keine passenden Prompts',
    ai_no_saved_prompts: 'Noch keine gespeicherten Prompts',
    ai_create_first_prompt: 'Erstellen Sie Ihren ersten Prompt',
    ai_used_times: '{n}x verwendet',
    ai_edit_title: 'Bearbeiten',
    ai_delete_title: 'Löschen',
    ai_form_title_label: 'Titel',
    ai_form_prompt_label: 'Prompt',
    ai_form_prompt_placeholder: 'Prompt-Vorlage eingeben...',
    ai_form_category_label: 'Kategorie',
    ai_form_category_placeholder: 'z.B. Schreiben, Programmieren, Analyse',
    ai_form_favorite_label: 'Als Favorit markieren',
    ai_cancel: 'Abbrechen',
    ai_save_changes: 'Änderungen speichern',
    ai_create_prompt: 'Prompt erstellen',
    ai_save_prompt_failed: 'Prompt konnte nicht gespeichert werden',
    ai_delete_prompt_confirm: 'Möchten Sie diesen Prompt wirklich löschen?',
    ai_delete_prompt_failed: 'Prompt konnte nicht gelöscht werden',
    ai_enter_title: 'Bitte geben Sie einen Titel ein',
    ai_enter_prompt: 'Bitte geben Sie einen Prompt ein',
    ai_filter_all: 'Alle',

    // AI – Relative times
    just_now: 'Gerade eben',
    minutes_ago: 'vor {n} Min.',
    hours_ago: 'vor {n} Std.',
    yesterday: 'Gestern',
    days_ago: 'vor {n} Tagen',
    today: 'Heute',
    this_week: 'Diese Woche',
    this_month: 'Dieser Monat',

    // AI – Message count
    no_messages: 'Keine Nachrichten',
    one_message: '1 Nachricht',
    n_messages: '{n} Nachrichten',

    // Searchable dropdown
    dropdown_searching: 'Suche...',
    dropdown_no_results: 'Keine Ergebnisse für „{q}“',
    dropdown_type_to_search: 'Zum Suchen tippen',
    dropdown_min_chars: 'Mindestens {n} Zeichen zum Suchen eingeben...',
    dropdown_none: 'Keine',
    dropdown_unknown: 'Unbekannt',
    dropdown_search_failed: 'Suche fehlgeschlagen: {message}',

    // Two-Factor Authentication
    twoFactorSetupTitle: 'Zwei-Faktor-Authentifizierung einrichten',
    twoFactorVerifyTitle: 'Zwei-Faktor-Authentifizierung',
    twoFactorStepScan: 'Scannen',
    twoFactorStepConfirm: 'Bestätigen',
    twoFactorStepSaveCodes: 'Codes speichern',
    twoFactorStepVerify: 'Verifizieren',
    twoFactorScanQrCode: 'Scannen Sie diesen QR-Code mit Ihrer Authenticator-App (Google Authenticator, Authy usw.)',
    twoFactorManualEntry: 'Scannen nicht möglich? Geben Sie diesen Schlüssel manuell ein:',
    twoFactorScannedQr: 'Ich habe den QR-Code gescannt',
    twoFactorEnterCodeConfirm: 'Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein, um die Einrichtung zu bestätigen:',
    twoFactorVerifyAndEnable: 'Verifizieren & Aktivieren',
    twoFactorSaveRecoveryCodes: 'Speichern Sie Ihre Wiederherstellungscodes!',
    twoFactorRecoveryCodesDesc: 'Diese Codes können verwendet werden, um auf Ihr Konto zuzugreifen, wenn Sie Ihr Authenticator-Gerät verlieren. Jeder Code kann nur einmal verwendet werden. Bewahren Sie sie an einem sicheren Ort auf.',
    twoFactorCopyAllCodes: 'Alle Codes kopieren',
    twoFactorCodesSaved: 'Ich habe diese Codes gespeichert',
    twoFactorEnterNewCode: 'Geben Sie einen neuen Code aus Ihrer Authenticator-App ein, um die Einrichtung abzuschließen:',
    twoFactorCompleteSetup: 'Einrichtung abschließen',
    twoFactorEnterCode: 'Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein:',
    twoFactorUseRecovery: 'Stattdessen Wiederherstellungscode verwenden',
    twoFactorUseAuthenticator: 'Stattdessen Authenticator-Code verwenden',
    twoFactorEnterRecovery: 'Geben Sie einen Ihrer Wiederherstellungscodes ein:',
    twoFactorVerifyRecovery: 'Mit Wiederherstellungscode verifizieren',
    twoFactorBackToLogin: 'Zurück zur Anmeldung',
    twoFactorSetupFailed: 'Initialisierung der 2FA-Einrichtung fehlgeschlagen. Bitte versuchen Sie sich erneut anzumelden.',
    twoFactorInvalidCode: 'Ungültiger Bestätigungscode. Bitte versuchen Sie es erneut.',
    twoFactorEnterSixDigit: 'Bitte geben Sie einen 6-stelligen Code ein',
    twoFactorEnterRecoveryCode: 'Bitte geben Sie einen Wiederherstellungscode ein',
    twoFactorEnabledSuccess: 'Zwei-Faktor-Authentifizierung erfolgreich aktiviert!',
    twoFactorVerifySuccess: 'Verifizierung erfolgreich!',
    twoFactorSecretCopied: 'Geheimschlüssel in Zwischenablage kopiert',
    twoFactorCodesCopied: 'Wiederherstellungscodes in Zwischenablage kopiert',

    // Clipboard
    phone_copied: 'Telefonnummer kopiert: {text}',

    // Contact card
    contact_click_copy: 'Zum Kopieren klicken',
    contact_view_details: 'Details anzeigen',
    contact_hide_details: 'Details ausblenden',
    contact_section_personal: 'Persönliche Informationen',
    contact_label_name: 'Name:',
    contact_label_position: 'Position:',
    contact_label_department: 'Abteilung:',
    contact_label_salutation: 'Anrede:',
    contact_label_academic_title: 'Akademischer Titel:',
    contact_section_contact: 'Kontaktinformationen',
    contact_label_emails: 'E-Mails:',
    contact_label_phones: 'Telefonnummern:',
    contact_label_linkedin: 'LinkedIn:',
    contact_phone_type: 'Telefon',
    contact_section_company: 'Unternehmensinformationen',
    contact_label_company: 'Unternehmen:',
    contact_label_branch: 'Branche:',
    contact_label_address: 'Adresse:',
    contact_recent_activities: 'Letzte Aktivitäten ({n})',
    contact_show_more_activities: '{n} weitere Aktivitäten anzeigen',
    contact_show_less: 'Weniger anzeigen',
    contact_untitled: 'Ohne Titel',
    contact_activity_open_title: 'Klicken zum Öffnen der Aktivität',
    not_applicable: 'k. A.',
  },
};

/**
 * Get the active language code ('en' or 'de').
 */
export function getLanguage() {
  const stored = localStorage.getItem(LANG_KEY);
  return SUPPORTED_LANGS.includes(stored) ? stored : 'en';
}

/**
 * Persist the language preference.
 * @param {string} lang - 'en' or 'de'
 */
export function setLanguage(lang) {
  if (SUPPORTED_LANGS.includes(lang)) {
    localStorage.setItem(LANG_KEY, lang);
  }
}

/**
 * Remove stored language (e.g. on logout).
 */
export function clearUserLanguage() {
  localStorage.removeItem(LANG_KEY);
}

/**
 * Translate a key, optionally interpolating `{placeholder}` tokens.
 *
 * @param {string} key - Translation key
 * @param {Object} [params] - Optional map of placeholder → value  e.g. { n: 5 }
 * @returns {string}
 */
export function t(key, params) {
  const lang = getLanguage();
  const dict = translations[lang] || translations.en;
  let str = dict[key] ?? translations.en[key] ?? key;

  if (params) {
    Object.entries(params).forEach(([placeholder, value]) => {
      str = str.replace(new RegExp(`\\{${placeholder}\\}`, 'g'), value);
    });
  }

  return str;
}
